import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getActiveProject, getProfile, postCheckin } from '../api';
import type { CheckinResponse, Project, UnitPreference } from '../types';
import { Field } from '../components/Field';
import { WeightInput } from '../components/WeightInput';
import { WaistInput } from '../components/WaistInput';
import { UnitToggle } from '../components/UnitToggle';
import { Spinner } from '../components/Spinner';

const MOODS = ['great', 'good', 'okay', 'tired', 'rough'];

export function CheckIn() {
  const navigate = useNavigate();
  const [unit, setUnit] = useState<UnitPreference>('metric');
  const [weight_kg, setWeightKg] = useState<number | null>(null);
  const [body_fat_pct, setBodyFatPct] = useState<string>('');
  const [waist_cm, setWaistCm] = useState<number | null>(null);
  const [steps, setSteps] = useState<string>('');
  const [calories, setCalories] = useState<string>('');
  const [workoutCompleted, setWorkoutCompleted] = useState<boolean>(false);
  const [mood, setMood] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<CheckinResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [project, setProject] = useState<Project | null>(null);

  useEffect(() => {
    getProfile()
      .then((p) => {
        if (p?.unit_preference) setUnit(p.unit_preference);
      })
      .catch(() => {
        /* non-fatal */
      });
    // Drives the post-submit "Reply to coach" CTA — repoints to the active
    // project's WorkoutChat, or hides if there's none (Brief C — coaching
    // is project-scoped, no anytime general chat).
    getActiveProject()
      .then((p) => setProject(p.project))
      .catch(() => {
        /* non-fatal — CTA just hides */
      });
  }, []);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        weight_kg,
        body_fat_pct: body_fat_pct === '' ? null : Number(body_fat_pct),
        waist_cm,
        steps: steps === '' ? null : Number(steps),
        calories: calories === '' ? null : Number(calories),
        workout_completed: workoutCompleted,
        ...(mood ? { mood } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      };
      const res = await postCheckin(payload);
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    const replies = result.replies ?? [];
    // Silent-save branch (§4.5): backend skips runCoach when coach_general
    // is off and returns only { checkin } — no replies, no analytics. We
    // detect that as "no replies key at all" so the older "saved but coach
    // unavailable" 207-multi-status branch (which sets replies: []) stays on
    // its own copy. The split keeps the messaging accurate.
    const silentSave = result.replies === undefined;

    // Mirrors PERSONA_VARIANTS in screens/Chat.tsx — kept inline rather than
    // imported because it's the only consumer and stays close to the markup.
    const VARIANTS: Record<string, string> = {
      good_cop: ' bubble--good-cop',
      bad_cop: ' bubble--bad-cop',
      therapist: ' bubble--therapist',
      scientist: ' bubble--scientist',
      nonna: ' bubble--nonna',
      drill_sergeant: ' bubble--drill-sergeant',
    };
    const variant = (slug: string): string => VARIANTS[slug] ?? '';

    if (silentSave) {
      return (
        <div className="shell">
          <header className="rise"><p className="eyebrow">Check-in</p><h1 className="h1">Logged ✓</h1></header>
          <div className="card card--elevated rise" style={{ marginTop: 16 }}>
            <p className="muted" style={{ lineHeight: 1.6, fontSize: 15, margin: 0 }}>
              Your check-in is saved. Coach is off — turn it on from the coach page when you want feedback.
            </p>
          </div>
          <div className="row rise" style={{ marginTop: 16, gap: 8 }}>
            <button type="button" className="btn btn--ghost" onClick={() => navigate('/dashboard')}>Dashboard</button>
            <button type="button" className="btn btn--primary" onClick={() => navigate('/coach')}>Coach page</button>
          </div>
        </div>
      );
    }

    return (
      <div className="shell">
        <header className="rise"><p className="eyebrow">Coach</p><h1 className="h1">Today's read</h1></header>
        {replies.length === 0 ? (
          <div className="card card--elevated rise" style={{ marginTop: 16 }}>
            <p className="muted" style={{ lineHeight: 1.6, fontSize: 15 }}>
              Saved your check-in. Coach feedback was unavailable.
            </p>
          </div>
        ) : (
          <div className="stack rise" style={{ marginTop: 16, gap: 12 }}>
            {replies.map((r, i) => (
              <div key={i} className={`bubble bubble--coach${variant(r.persona)}`} style={{ maxWidth: '100%' }}>
                <div className="bubble__persona">
                  <span>{r.label}</span>
                </div>
                <div>{r.content}</div>
              </div>
            ))}
          </div>
        )}
        <div className="row rise" style={{ marginTop: 16, gap: 8 }}>
          <button type="button" className="btn btn--ghost" onClick={() => navigate('/dashboard')}>Dashboard</button>
          {project ? (
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => navigate(`/projects/${project.id}/workout-chat`)}
            >
              Reply to coach
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <form className="shell" onSubmit={onSubmit}>
      <header className="row row--between rise">
        <div>
          <p className="eyebrow">Daily</p>
          <h1 className="h1">Check-in</h1>
        </div>
        <UnitToggle value={unit} onChange={setUnit} />
      </header>

      <section className="section rise">
        <WeightInput
          label="Weight today"
          value_kg={weight_kg}
          onChange={setWeightKg}
          unit={unit}
          hint="Same time of day if you can — first thing in the morning is gold."
        />
        <div className="grid-2">
          <Field label="Body fat % (optional)">
            <input className="input" type="number" inputMode="decimal" step="0.1" min="0" max="60" placeholder="e.g. 18.5" value={body_fat_pct} onChange={(e) => setBodyFatPct(e.target.value)} />
          </Field>
          <WaistInput
            label="Waist (optional)"
            value_cm={waist_cm}
            onChange={setWaistCm}
            unit={unit}
          />
        </div>
        <div className="grid-2">
          <Field label="Steps">
            <input className="input" type="number" inputMode="numeric" min="0" placeholder="e.g. 9400" value={steps} onChange={(e) => setSteps(e.target.value)} />
          </Field>
          <Field label="Calories">
            <input className="input" type="number" inputMode="numeric" min="0" placeholder="e.g. 2050" value={calories} onChange={(e) => setCalories(e.target.value)} />
          </Field>
        </div>
      </section>

      <section className="section rise">
        <Field label="Did you train today?">
          <div className="toggle">
            <button type="button" className={`toggle__btn${workoutCompleted ? ' toggle__btn--on' : ''}`} onClick={() => setWorkoutCompleted(true)}>Yes</button>
            <button type="button" className={`toggle__btn${!workoutCompleted ? ' toggle__btn--on' : ''}`} onClick={() => setWorkoutCompleted(false)}>No</button>
          </div>
        </Field>

        <Field label="How are you feeling?">
          <div className="chips">
            {MOODS.map((m) => (
              <button
                key={m}
                type="button"
                className={`chip${mood === m ? ' chip--selected' : ''}`}
                onClick={() => setMood(mood === m ? null : m)}
              >
                {m}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Notes (optional)" hint="Anything the coach should know — bad sleep, social meal, niggle.">
          <textarea
            className="input"
            placeholder="e.g. Slept 6h, big dinner out last night, knee a bit sore."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
          />
        </Field>
      </section>

      {error ? <div className="errorbar rise">{error}</div> : null}
      <button type="submit" className="btn btn--primary btn--lg btn--full rise" disabled={submitting}>
        {submitting ? (<><Spinner /> Saving…</>) : 'Submit check-in'}
      </button>
    </form>
  );
}
